import type { IDynamicFilterControl } from '@gp/components/DynamicFilter/components/FilterControl/types';
import type { IDynamicFilterField } from '@gp/components/DynamicFilter/types';
import FilterCommon from '@gp/components/FilterCommon';
import NewFeatureTipsContent from '@gp/components/NewFeatureTipsContent';
import RangePickerButton from '@gp/components/RangePickerButton';
import { CONSTANT_TEXTS } from '@gp/constants/const';
import { KEYS_MAPPING, NAMES_MAPPING } from '@gp/constants/field';
import { COMMON_SELECT_MULTIPLE_CONTROL_CFG } from '@gp/constants/select';
import useTipsView from '@gp/hooks/useTipsView';
import type { IRecord } from '@gp/types/global';
import type {
  IProjectGRActionItemsFilterConditionsVO,
  IProjectGRActionItemsFilterVO,
} from '@gp/types/grActionItems';
import type { IBaseProps, INullableMoment } from '@home/types/common';
import { cloneDeep, isEqual, merge } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

interface IGRActionItemsFilterProps extends IBaseProps {
  loading?: boolean;
  data?: IProjectGRActionItemsFilterConditionsVO | null;
  value?: IProjectGRActionItemsFilterVO;
  onChange?: (payload: IProjectGRActionItemsFilterVO) => void;
}

const GR_ACTION_ITEMS_FILTER_STATUS_TIPS = `${CONSTANT_TEXTS.CANCELED} and ${CONSTANT_TEXTS.ON_HOLD} action items are displayed by default.`;

const DEFAULT_CONTROLS: IRecord<IDynamicFilterControl> = {
  dueDate: {},
  pteam: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    listWidth: 260,
    options: [],
  },
  business_owner: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    matchButtonWidth: false,
    listWidth: 260,
    options: [],
  },
  studio: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    matchButtonWidth: false,
    listWidth: 260,
    options: [],
  },
  gate_review: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    listWidth: 340,
    options: [],
  },
  owner: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    options: [],
  },
  status: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    searchable: false,
    options: [],
  },
  stage: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    searchable: false,
    options: [],
  },
};

const GRActionItemsFilter: React.FC<IGRActionItemsFilterProps> = (props) => {
  const {
    style,
    className,
    loading = true,
    data,
    value: valueProps,
    onChange: onChangeProps,
    allFilterOptionsValues,
    onReset,
    scrollBody,
    headerTitle,
    getSearchList,
    showRightBtn,
    currentPage,
    updateDeps,
    handleSearch,
    formatSearchResults,
    searchVal,
    searchList,
    selectedValue,
    setSelectedValue,
    setSearchList,
    setSearchVal,
  } = props;

  const { isTipsView, handleViewedTips } = useTipsView(
    'pipeline_gr_action_items_filter_status',
  );

  const controls = useMemo<IRecord<IDynamicFilterControl>>(() => {
    if (loading) return DEFAULT_CONTROLS;
    const {
      pteam: pteamOptions,
      business_owner: boOptions,
      studio: studioMappings,
      gate_review: gateReviewOptions,
      owner: ownerOptions,
      status: statusOptions,
      stage: stageOptions,
      project_id: cascadeProjectOptions,
      dueDate,
    } = data || {};
    const { min_date: minDate, max_date: maxDate } = dueDate || {};
    const controlsTmp = cloneDeep(DEFAULT_CONTROLS);
    const minDateM: INullableMoment = !minDate ? null : moment(minDate);
    const maxDateM: INullableMoment = !maxDate ? null : moment(maxDate);

    return merge(controlsTmp, {
      dueDate: {
        startOfTime: minDate,
        endOfTime: maxDate,
        showShortcut: true,
        disabledDate: {
          date: (m: moment.Moment): boolean => {
            if (!minDateM?.isValid() && !maxDateM?.isValid()) return true;
            if (!minDateM?.isValid()) return m?.isSameOrBefore(maxDateM);
            if (!maxDateM?.isValid()) return m?.isSameOrAfter(minDateM);
            return m?.isSameOrAfter(minDateM) && m?.isSameOrBefore(maxDateM);
          },
        },
      },
      project_id: {
        options: cascadeProjectOptions,
        allValues: allFilterOptionsValues?.project_id || [],
        btnTitle: 'P-team/BO/Studio/Project',
      },
      gate_review: {
        options: gateReviewOptions,
      },
      owner: {
        options: ownerOptions,
      },
      status: {
        options: statusOptions,
        bubbleProps: {
          style: { maxWidth: 464 },
          visible: !isTipsView ? true : undefined,
          content: isTipsView ? undefined : (
            <NewFeatureTipsContent
              description={GR_ACTION_ITEMS_FILTER_STATUS_TIPS}
              onClose={handleViewedTips}
            />
          ),
        },
      },
      stage: {
        options: stageOptions,
      },
    });
  }, [loading, data, isTipsView, handleViewedTips]);

  const [value, setValue] = useState<IProjectGRActionItemsFilterVO>({
    dueDate: {
      dateType: 'date',
      startDate: '',
      endDate: '',
    },
    project_id: [],
    gate_review: [],
    owner: [],
    status: [],
    stage: [],
  });

  const fields = useMemo<IDynamicFilterField[]>(() => {
    const { dueDate } = value || {};
    const { project_id: projectOptions } = data || {};
    const projectDisplay = loading
      ? false
      : Array.isArray(projectOptions) && projectOptions.length > 0;
    return [
      {
        key: 'dueDate',
        type: 'rangePicker',
        label: (
          <RangePickerButton.CommonDateRangePickerButton
            name="DUE"
            value={dueDate}
          />
        ),
        saveKey: 'gr_dueDate',
      },
      {
        key: KEYS_MAPPING.CASCADE_PROJECT,
        type: 'cascadeProject',
        display: projectDisplay,
        label: NAMES_MAPPING.CASCADE_PROJECT,
      },
      { key: 'gate_review', type: 'selectMultiple', label: 'Review Type' },
      { key: 'owner', type: 'selectMultiple', label: 'Owner' },
      {
        key: 'status',
        type: 'selectMultiple',
        label: 'Status',
        saveKey: 'gr_status',
      },
      { key: 'stage', type: 'selectMultiple', label: 'Stage' },
    ];
  }, [value]);

  const handleChange = (payload: IRecord<any>) => {
    const valueTmp = { ...value, ...payload };
    setValue(valueTmp);
  };

  const handleConfirm = () => {
    onChangeProps?.(value);
  };

  useEffect(() => {
    if (!isEqual(value, valueProps)) {
      setValue({ ...value, ...valueProps });
    }
  }, [valueProps]);

  return (
    <FilterCommon
      style={style}
      className={className}
      controls={controls}
      value={value}
      onChange={handleChange}
      isNeedDiff={true}
      fields={fields}
      confirm={handleConfirm}
      onReset={onReset}
      loading={loading}
      getSearchList={getSearchList}
      scrollBody={scrollBody}
      headerTitle={headerTitle}
      showRightBtn={showRightBtn}
      currentPage={currentPage}
      updateDeps={updateDeps}
      handleSearch={handleSearch}
      formatSearchResults={formatSearchResults}
      searchVal={searchVal}
      searchList={searchList}
      filterSearchHeight={110}
      filterHeight={86}
      selectedValue={selectedValue}
      setSelectedValue={setSelectedValue}
      setSearchList={setSearchList}
      setSearchVal={setSearchVal}
    />
  );
};

export default GRActionItemsFilter;
